// Protects email addresses from harvesters: addresses coming from the CMS are replaced with
// tokens before rendering and before the Nuxt payload (__NUXT_DATA__) is written. They are only
// turned back into regular mailto links in the browser (EmailLink.vue,
// plugins/emailObfuscation.client.ts), so the delivered HTML never contains a plain address.

export const EMAIL_TOKEN_PREFIX = 'obf:'
export const EMAIL_PLACEHOLDER = 'E-Mail'

// Field name of dedicated address fields (team members, globals), rendered via EmailLink
const EMAIL_FIELD = 'email'

// Unicode-aware (e.g. jürg.müller@…) and allowing apostrophes inside the local part
// (o'brien@…), so addresses are never matched only partially. The top-level domain must not
// be a file extension, so names like logo@2x.png are no address.
const FILE_EXTENSIONS =
  'png|jpe?g|gif|svg|webp|avif|heic|pdf|docx?|xlsx?|pptx?|csv|zip|mp3|mp4|webm'
const LOCAL_CHAR = `[\\p{L}\\p{N}._%+-]`
const EMAIL = new RegExp(
  `${LOCAL_CHAR}(?:[\\p{L}\\p{N}._%+'-]*${LOCAL_CHAR})?@[\\p{L}\\p{N}-]+(?:\\.[\\p{L}\\p{N}-]+)*\\.(?!(?:${FILE_EXTENSIONS})(?!\\p{L}))\\p{L}{2,}(?![\\p{L}\\p{N}-]|\\.[\\p{L}\\p{N}])`,
  'giu'
)
// Opening tag of a mailto link; the href value may contain the other quote character (o'brien@…)
const MAILTO_TAG = /^<a\b([^>]*?)\s+href=(["'])mailto:((?:(?!\2)[^>])*)\2([^>]*)>$/i
// Tokens of an HTML string: script/style block (open tag, name, code, close tag), comment, tag, text
const HTML_TOKEN =
  /(<(script|style)\b[^>]*>)([\s\S]*?)(<\/\2\s*>)|(<!--[\s\S]*?-->)|(<[^>]*>)|([^<]+)/gi
// Script types with JS or JSON content, where @ is a valid escape for "@"
const CODE_SCRIPT_TYPE =
  /^(|text\/javascript|module|importmap|application\/(javascript|json|ld\+json))$/i
const URL_OR_PATH = /^(?:[a-z][a-z\d+.-]*:\/\/|\/)\S*$/i
// Statamic ids such as assets::downloads/flyer.pdf
const STATAMIC_ID = /^[\w-]+::\S*$/

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  quot: '"',
  apos: "'",
  lt: '<',
  gt: '>',
}

const decodeEntities = (value: string) =>
  value.replace(/&(#x[\da-f]+|#\d+|[a-z]+);/gi, (entity, code: string) => {
    if (code[0] !== '#') return NAMED_ENTITIES[code.toLowerCase()] ?? entity
    const hex = code[1].toLowerCase() === 'x'
    return String.fromCodePoint(parseInt(code.slice(hex ? 2 : 1), hex ? 16 : 10))
  })

const reverse = (value: string) => [...value].reverse().join('')

const toBase64 = (value: string) =>
  btoa(String.fromCharCode(...new TextEncoder().encode(value)))

const fromBase64 = (value: string) =>
  new TextDecoder().decode(Uint8Array.from(atob(value), (c) => c.charCodeAt(0)))

// target = everything after "mailto:", i.e. one or more comma-separated addresses including
// an optional ?subject=…
export const encodeEmail = (target: string) =>
  EMAIL_TOKEN_PREFIX + toBase64(reverse(target))

// Accepts tokens and, as a fallback, plain addresses
export const decodeEmail = (value: string) => {
  if (!value.startsWith(EMAIL_TOKEN_PREFIX)) return value
  try {
    return reverse(fromBase64(value.slice(EMAIL_TOKEN_PREFIX.length)))
  } catch {
    return ''
  }
}

// Visible text for a mailto target: addresses without query, separated by ", "
export const emailAddress = (target: string) =>
  target
    .split('?')[0]
    .split(',')
    .map((address) => address.trim())
    .join(', ')

// Placeholder that the client plugin replaces with the address. Rendered as <span> inside
// an existing link, since links must not be nested.
const placeholder = (address: string, tag: 'a' | 'span') =>
  `<${tag} data-email="${encodeEmail(address)}" data-email-text>${EMAIL_PLACEHOLDER}</${tag}>`

// Addresses in code (e.g. a widget config) keep working but are no plain text: the "@" is
// written as an escape sequence of the language (JS/JSON: @, CSS: \40)
const escapeAddresses = (code: string, at: string) =>
  code.replace(EMAIL, (address: string) => address.replace('@', at))

function obfuscateCode(open: string, name: string, code: string, close: string) {
  if (name.toLowerCase() === 'style') return open + escapeAddresses(code, '\\40 ') + close
  const type = open.match(/\btype\s*=\s*(["']?)([^"'\s>]*)\1/i)?.[2] ?? ''
  // other script types (e.g. text/template) are left as they are
  return CODE_SCRIPT_TYPE.test(type)
    ? open + escapeAddresses(code, '\\u0040') + close
    : open + code + close
}

// Single pass over all tokens, so the link depth is tracked across comments and code blocks
function obfuscateHtml(html: string) {
  let linkDepth = 0
  return html.replace(
    HTML_TOKEN,
    (_, open, name, code, close, comment, tag, text) => {
      if (open) return obfuscateCode(open, name, code, close)
      // placeholder markup is inert inside a comment
      if (comment) {
        return comment.replace(EMAIL, (address: string) => placeholder(address, 'span'))
      }
      if (tag) {
        if (/^<\/a\s*>/i.test(tag)) linkDepth--
        if (!/^<a\b/i.test(tag)) return tag
        linkDepth++
        // mailto links: move the target into a token. The link text stays as it is, addresses
        // in it are replaced individually, so wording and formatting are preserved.
        return tag.replace(
          MAILTO_TAG,
          (_match: string, before: string, _quote: string, target: string, after: string) =>
            `<a${before}${after} data-email="${encodeEmail(decodeEntities(target))}">`
        )
      }
      if (!text.includes('@')) return text
      // Escaped apostrophes (o&#039;brien@…) are unescaped first, which is valid in text
      // content and keeps the address in one piece
      return text
        .replace(/&(?:#0*39|apos);/gi, "'")
        .replace(EMAIL, (address: string) =>
          placeholder(address, linkDepth > 0 ? 'span' : 'a')
        )
    }
  )
}

function obfuscateString(value: string, key: string) {
  if (!value.includes('@')) return value

  // Address field → token for EmailLink. Whitespace around the @ and surrounding text are
  // tolerated, several addresses become one mailto with multiple recipients.
  if (key === EMAIL_FIELD) {
    const addresses = value.replace(/\s*@\s*/g, '@').match(EMAIL)
    return addresses ? encodeEmail(addresses.join(',')) : value
  }

  const trimmed = value.trim()
  // mailto from link fields → token as href, resolved by the client plugin
  if (/^mailto:/i.test(trimmed)) return encodeEmail(trimmed.slice(7))
  // leave URLs, paths and Statamic ids untouched (e.g. map links containing /@47.37,8.04)
  if (URL_OR_PATH.test(trimmed) || STATAMIC_ID.test(trimmed)) return value

  return obfuscateHtml(value)
}

function obfuscate(value: unknown, key: string): unknown {
  if (typeof value === 'string') return obfuscateString(value, key)
  if (Array.isArray(value)) return value.map((item) => obfuscate(item, key))
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, obfuscate(v, k)])
    )
  }
  return value
}

export const obfuscateEmails = <T>(data: T): T => obfuscate(data, '') as T

// Browser only: replaces the placeholders created by obfuscateEmails with regular mailto
// links. Covers addresses in HTML content and mailto links from link fields.
export const EMAIL_SELECTOR = `[data-email^="${EMAIL_TOKEN_PREFIX}"], a[href^="${EMAIL_TOKEN_PREFIX}"]`

export function revealEmail(el: Element) {
  const target = decodeEmail(
    el.getAttribute('data-email') ?? el.getAttribute('href') ?? ''
  )
  if (!target) return

  if (el.tagName === 'A') el.setAttribute('href', `mailto:${target}`)
  if (el.hasAttribute('data-email-text')) el.textContent = emailAddress(target)
  el.removeAttribute('data-email')
  el.removeAttribute('data-email-text')
}

// root can be an element, a document, a fragment or a shadow root; <template> content included
export function revealEmails(root: Element | Document | DocumentFragment) {
  if (root instanceof Element && root.matches(EMAIL_SELECTOR)) revealEmail(root)
  root.querySelectorAll(EMAIL_SELECTOR).forEach(revealEmail)
  root
    .querySelectorAll('template')
    .forEach((template) => revealEmails(template.content))
}
